import { useTranslation } from "react-i18next";
import { AlertTriangle, Eye, Pause, Play, RotateCcw, X } from "lucide-react";

import type { BlsChannel } from "../../application/useBlsChannel";
import {
    BLS_DOT_COLORS,
    BLS_DOT_SIZES,
    BLS_MODES,
    BLS_PATHS,
    type BlsSettings,
    type BlsState,
} from "../../domain/blsChannel";
import { MAX_CYCLES_PER_MINUTE, MIN_CYCLES_PER_MINUTE, clampCyclesPerMinute } from "../../domain/blsMotion";
import { usePassCount, useBlsSettings } from "../../application/useBlsControls";
import type { EmdrSetRecorder } from "../../domain/emdrSets";
import BlsStimulus from "./BlsStimulus";
import EmdrSetForm from "./EmdrSetForm";

type Props = {
    channel: BlsChannel;
    /** Put the panel away. Closing ends the set for both people. */
    onClose: () => void;
    /**
     * Where a set's ratings are saved. Absent where there is no individual
     * session to save them to, and then the panel only runs the stimulation.
     */
    recorder?: EmdrSetRecorder;
};

const selectClass =
    "min-h-9 w-full rounded-lg border border-room-line bg-room-active px-2 text-sm text-room-ink";

/**
 * The therapist's side of a set.
 *
 * It sits beside the video rather than over it: the therapist's job during a
 * set is watching the patient's eyes, and the preview is there to confirm what
 * the patient is seeing, not to be watched instead.
 */
export default function BlsControlPanel({ channel, onClose, recorder }: Props) {
    const { t } = useTranslation();
    const { state } = channel;
    const { settings, change } = useBlsSettings(channel);
    const passes = usePassCount(channel);

    const close = () => {
        channel.stop();
        onClose();
    };

    const preview: BlsState = state ?? { settings, running: false, cycles: 0, atEpochMs: 0 };

    return (
        <section
            aria-labelledby="bls-panel-title"
            className="flex max-h-full min-h-0 w-full flex-col gap-3 overflow-y-auto overscroll-contain rounded-2xl border border-room-line/60 bg-room-raised/90 p-3 backdrop-blur-md"
            data-testid="bls-panel"
        >
            <header className="flex items-start justify-between gap-3">
                <h2 id="bls-panel-title" className="flex items-center gap-2 text-sm font-semibold text-room-ink">
                    <Eye size={16} aria-hidden="true" />
                    {t("emdr.panelTitle")}
                </h2>
                <button
                    type="button"
                    onClick={close}
                    aria-label={t("emdr.close")}
                    className="flex size-9 items-center justify-center rounded-lg text-room-ink-muted hover:bg-room-active"
                >
                    <X size={16} aria-hidden="true" />
                </button>
            </header>

            {/* First, not last: on a laptop the panel is shorter than its
                contents, and a Start button below the fold reads as a tool
                that does nothing — the patient never sees a dot. */}
            <div className="flex flex-wrap items-center gap-2">
                {!state && (
                    <button type="button" onClick={() => channel.start(settings)} className="room-control room-control-primary">
                        <Play size={16} aria-hidden="true" />
                        <span>{t("emdr.start")}</span>
                    </button>
                )}
                {state?.running && (
                    <button type="button" onClick={channel.pause} className="room-control">
                        <Pause size={16} aria-hidden="true" />
                        <span>{t("emdr.pause")}</span>
                    </button>
                )}
                {state && !state.running && (
                    <>
                        <button type="button" onClick={channel.resume} className="room-control room-control-primary">
                            <Play size={16} aria-hidden="true" />
                            <span>{t("emdr.resume")}</span>
                        </button>
                        <button type="button" onClick={() => channel.start(settings)} className="room-control">
                            <RotateCcw size={16} aria-hidden="true" />
                            <span>{t("emdr.newSet")}</span>
                        </button>
                    </>
                )}
                <p className="ml-auto text-xs text-room-ink-muted" aria-live="polite">
                    {t("emdr.passes")}{" "}
                    <span data-testid="bls-passes" className="tabular-nums text-sm font-semibold text-room-ink">
                        {passes}
                    </span>
                </p>
            </div>

            {!state && (
                <p className="rounded-lg bg-room-accent-tint px-3 py-2 text-xs text-room-ink" data-testid="bls-not-started">
                    {t("emdr.notStartedHint")}
                </p>
            )}

            <p className="flex gap-2 text-xs text-room-ink-muted">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-room-warn" aria-hidden="true" />
                {t("emdr.photosensitivity")}
            </p>

            {channel.haltedByPatient && (
                <p role="alert" className="rounded-lg bg-room-live-tint px-3 py-2 text-sm text-room-live">
                    {t("emdr.patientStopped")}
                </p>
            )}
            {channel.transportFailed && (
                <p role="status" className="text-xs text-room-warn">
                    {t("emdr.transportFailed")}
                </p>
            )}

            <div className="flex flex-col gap-1">
                <span className="text-xs text-room-ink-muted">{t("emdr.preview")}</span>
                <BlsStimulus state={preview} cyclesNow={channel.cyclesNow} className="h-20 w-full rounded-lg" />
            </div>

            <div role="group" aria-label={t("emdr.mode.label")} className="flex gap-1 rounded-xl bg-room-active/60 p-1">
                {BLS_MODES.map((mode) => (
                    <button
                        key={mode}
                        type="button"
                        aria-pressed={settings.mode === mode}
                        onClick={() => change({ mode })}
                        className={`min-h-9 min-w-0 flex-1 truncate rounded-lg px-2 text-xs font-medium ${
                            settings.mode === mode ? "bg-room-accent text-white" : "text-room-ink hover:bg-room-hover"
                        }`}
                    >
                        {t(`emdr.mode.${mode}`)}
                    </button>
                ))}
            </div>

            <label className="flex flex-col gap-1 text-xs text-room-ink-muted">
                <span className="flex justify-between">
                    <span>{t("emdr.speed.label")}</span>
                    <span className="tabular-nums text-room-ink">
                        {t("emdr.speed.value", { count: settings.cyclesPerMinute })}
                    </span>
                </span>
                <input
                    type="range"
                    min={MIN_CYCLES_PER_MINUTE}
                    max={MAX_CYCLES_PER_MINUTE}
                    step={1}
                    value={settings.cyclesPerMinute}
                    aria-label={t("emdr.speed.label")}
                    onChange={(event) =>
                        change({ cyclesPerMinute: clampCyclesPerMinute(Number(event.target.value)) })
                    }
                    className="accent-room-accent"
                />
            </label>

            <div className="grid grid-cols-3 gap-2">
                <label className="flex flex-col gap-1 text-xs text-room-ink-muted">
                    {t("emdr.path.label")}
                    <select
                        value={settings.path}
                        onChange={(event) => change({ path: event.target.value as BlsSettings["path"] })}
                        className={selectClass}
                    >
                        {BLS_PATHS.map((path) => (
                            <option key={path} value={path}>
                                {t(`emdr.path.${path}`)}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-xs text-room-ink-muted">
                    {t("emdr.size.label")}
                    <select
                        value={settings.dotSize}
                        onChange={(event) => change({ dotSize: event.target.value as BlsSettings["dotSize"] })}
                        className={selectClass}
                    >
                        {BLS_DOT_SIZES.map((size) => (
                            <option key={size} value={size}>
                                {t(`emdr.size.${size}`)}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-xs text-room-ink-muted">
                    {t("emdr.color.label")}
                    <select
                        value={settings.dotColor}
                        onChange={(event) => change({ dotColor: event.target.value as BlsSettings["dotColor"] })}
                        className={selectClass}
                    >
                        {BLS_DOT_COLORS.map((color) => (
                            <option key={color} value={color}>
                                {t(`emdr.color.${color}`)}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="flex flex-col gap-1">
                <label className="flex min-h-9 items-center gap-2 text-sm text-room-ink">
                    <input
                        type="checkbox"
                        checked={settings.tactile}
                        onChange={(event) => change({ tactile: event.target.checked })}
                        className="size-4 accent-room-accent"
                    />
                    {t("emdr.tactile.label")}
                </label>
                <p className="text-xs text-room-ink-muted">{t(controllersCopy(channel.patientControllers))}</p>
            </div>

            {recorder && <EmdrSetForm state={state} recorder={recorder} />}
        </section>
    );
}

/** What the therapist is told about the controllers in the patient's hands. */
function controllersCopy(controllers: number | null): string {
    if (controllers === null) return "emdr.tactile.unknown";
    if (controllers === 0) return "emdr.tactile.none";
    return controllers === 1 ? "emdr.tactile.one" : "emdr.tactile.two";
}
